import { ClipboardList, Eye, LucideIcon, Maximize2, SquarePlay, Target, Timer } from 'lucide-react';
import { FC, ReactNode } from 'react';

interface Step {
  icon: LucideIcon;
  title: string;
  image: string;
  body: ReactNode;
  highlight?: boolean;
  badge?: string;
  whyItMatters?: string;
}

const STEPS: Step[] = [
  {
    icon: Eye,
    title: 'About the Skill',
    image: './learn-experience-modal-3-about.png',
    body: (
      <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
        <span className="tw-font-bold">Builds context for what students are about to learn.</span> Students see a
        description of the skill, the skills it connects to, real-life applications, and key vocabulary. A good place
        for students to preview before diving in.
      </p>
    ),
  },
  {
    icon: SquarePlay,
    title: 'Worked Example Video',
    image: './learn-experience-modal-3-worked-example.png',
    body: (
      <>
        <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
          <span className="tw-font-bold">A short video walks through a complete example of the skill.</span> Available
          with English and Spanish closed captions. Students need headphones since the video has audio.
        </p>
        <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
          Encourage students <span className="tw-font-bold">to write down each step</span> as they watch so they can
          refer back when practicing.
        </p>
      </>
    ),
  },
  {
    icon: ClipboardList,
    title: 'Practice Problems',
    image: './learn-experience-modal-3-practice-problems.png',
    body: (
      <>
        <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
          <span className="tw-font-bold ">About 12 problems, ordered easy to hard.</span> The goal is practice, not
          completion. Students should <span className="tw-font-bold ">work these out on paper</span> and try one problem
          from the beginning, middle, and end of the set.
        </p>
        <p className="tw-m-0 tw-font-bold tw-font-atkinson tw-text-neutral-700 tw-text-base">
          If students are solving the problems towards the end, they are ready for the Skill Challenge and should move
          on.
        </p>
      </>
    ),
  },
  {
    icon: Target,
    title: 'Skill Challenge',
    image: './learn-experience-modal-3-skill-challenge.png',
    highlight: true,
    badge: 'NEVER SKIP!',
    whyItMatters:
      'Each result recalibrates what we know about this student across every connected skill and personalizes their Roadmap accordingly.',
    body: (
      <>
        <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
          <span className="tw-font-bold">5 questions that assess understanding.</span> Takes about 10 minutes. Students
          work independently and must complete it to progress past the skill.
        </p>
        <p className="tw-m-0 tw-font-atkinson tw-text-neutral-700 tw-text-base">
          Students are encouraged to <span className="tw-font-bold">complete a Skill Challenge each time</span> they
          engage in Roadmaps -- it&apos;s ok if they don&apos;t pass the first time!
        </p>
      </>
    ),
  },
];

interface Props {
  onExpandImage: (src: string) => void;
}

const ThirdPageUI: FC<Props> = ({ onExpandImage }) => {
  return (
    <div className="tw-flex tw-flex-col tw-gap-y-6">
      <div className="tw-flex tw-flex-row tw-gap-x-3 tw-items-center">
        <h1 className="tw-text-xl tw-font-sora tw-font-bold tw-text-neutral-950">
          Each skill follows the same 4-step cycle.
        </h1>
        <div className="tw-rounded-2xl tw-p-3 tw-flex tw-flex-row tw-gap-x-4 tw-bg-sky-50 tw-items-center">
          <span className="tw-border-2 tw-border-solid tw-border-sky-400 tw-bg-sky-500 tw-text-white tw-text-xs tw-font-bold tw-rounded-md tw-px-2 tw-py-1 tw-flex tw-flex-row tw-gap-x-1 tw-items-center tw-font-atkinson">
            <Timer size={12} className="tw-text-white" />
            TIME PER SKILL
          </span>
          <p className="tw-m-0 tw-font-atkinson tw-font-bold tw-text-sm tw-text-sky-600">
            30–45 minutes <span className="tw-text-sky-900">to complete</span>
          </p>
        </div>
      </div>

      <div className="tw-flex tw-flex-col">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isFirst = index === 0;
          const isLast = index === STEPS.length - 1;

          return (
            <div key={step.title} className="tw-flex tw-flex-row tw-gap-x-4">
              {/* Timeline column */}
              <div className="tw-flex tw-flex-col tw-items-center">
                <div className={`tw-w-[4px] tw-flex-1 ${isFirst ? 'tw-bg-transparent' : 'tw-bg-stone-100'}`} />
                <div className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-3 tw-border-solid tw-border-stone-100 tw-bg-white tw-text-sm tw-font-bold tw-text-stone-500 tw-font-atkinson">
                  {index + 1}
                </div>
                <div className={`tw-w-[4px] tw-flex-1 ${isLast ? 'tw-bg-transparent' : 'tw-bg-stone-100'}`} />
              </div>

              {/* Card */}
              <div
                className={`tw-relative tw-my-2 tw-flex tw-flex-1 tw-flex-row tw-border-4 tw-rounded-xl tw-border-solid ${
                  step.highlight ? 'tw-border-stone-600' : ' tw-border-stone-100'
                }`}
              >
                {step.badge && (
                  <span className="tw-absolute tw-rotate-[353deg] -tw-top-3 tw-left-4 tw-rounded-md tw-bg-neutral-800 tw-px-2 tw-py-1 tw-text-[10px] tw-font-bold tw-uppercase tw-leading-none tw-text-white tw-font-atkinson">
                    {step.badge}
                  </span>
                )}

                <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-3 tw-py-4 tw-px-8 tw-bg-stone-0 tw-rounded-l-xl  ">
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-x-2">
                    <span className="tw-p-1 tw-rounded-lg tw-bg-sky-100 tw-leading-none">
                      <Icon size={20} className="tw-text-sky-800" />
                    </span>
                    <h1 className="tw-m-0 tw-text-lg tw-font-bold tw-font-atkinson tw-text-neutral-900">
                      {step.title}
                    </h1>
                  </div>

                  <div className="tw-flex tw-flex-col tw-gap-y-3 tw-text-sm tw-text-neutral-700">{step.body}</div>

                  {step.whyItMatters && (
                    <div className="tw-bg-sky-50 tw-p-3 tw-border-2 tw-rounded-lg tw-border-solid tw-border-sky-200">
                      <p className="tw-m-0 tw-mb-2 tw-text-sm tw-font-bold tw-font-atkinson tw-text-neutral-950">
                        Why it matters?
                      </p>
                      <p className="tw-m-0 tw-text-sm tw-font-atkinson tw-text-neutral-950">{step.whyItMatters}</p>
                    </div>
                  )}
                </div>

                {/* Screenshot */}
                <div className="tw-flex tw-w-[220px] tw-shrink-0 tw-items-center tw-justify-center tw-py-4 tw-px-8 tw-bg-stone-50 tw-rounded-r-xl ">
                  <button
                    type="button"
                    onClick={() => onExpandImage(step.image)}
                    aria-label={`Expand ${step.title} screenshot`}
                    className="tw-group tw-relative tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
                  >
                    <img
                      src={step.image}
                      alt={step.title}
                      className="tw-w-full tw-rounded-md tw-border tw-border-solid tw-border-neutral-200 tw-object-contain tw-shadow-sm"
                    />
                    <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-black/40 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
                      <Maximize2 size={20} className="tw-text-white" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ThirdPageUI;
